import { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import useAuth from 'src/hooks/useAuth';
import actionListaClientesCanais from 'src/actions/clientes/actionListaClientesCanais';
import actionListaCanaisPorCliente from 'src/actions/clientes/actionListaCanaisPorCliente';
import v2_actionTotalCtwa from 'src/actions/v2/dashboard/relatorios/v2_actionTotalCtwa';
import v2_actionCtwaDetalhes from 'src/actions/v2/dashboard/relatorios/v2_actionCtwaDetalhes';
import v2_actionCtwaAnuncios from 'src/actions/v2/dashboard/relatorios/v2_actionCtwaAnuncios';
import v2_actionTotalTickets from 'src/actions/v2/dashboard/relatorios/v2_actionTotalTickets';

import Iconify from 'src/components/Iconify';
import MktLoading from 'src/sections/@dashboard/marketing/components/MktLoading';
import MktEmptyState from 'src/sections/@dashboard/marketing/components/MktEmptyState';
import PeriodFilter from 'src/sections/@dashboard/marketing/components/PeriodFilter';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/@/components/ui/select';

import { BarRow, ColumnChart, fmtInt, fmtPct, ratio } from 'src/sections/@dashboard/home/metricas/MetricasUI';
import { T } from 'src/pages/dashboard/Painel/Painel.design';
import { MKT_CTWA_DESIGN as d } from 'src/pages/dashboard/Marketing/MarketingCtwa.design';

import CtwaAnuncioCard from './CtwaAnuncioCard';
import CtwaAnuncioList from './CtwaAnuncioList';
import CtwaAnuncioChatsDrawer from './CtwaAnuncioChatsDrawer';
import CtwaWabaHero from './CtwaWabaHero';
import {
    filtrarCanaisWhatsappBusiness,
    montarQuerysCtwa,
    montarQuerysCtwaAnuncios,
    periodoParaDatas,
    rotuloDia,
    rotuloDiaCompleto,
    rotuloTipo,
} from './ctwa.utils';

const ANUNCIOS_PAGE_SIZE = 12;

function inferirPeriodo(inicio, final) {
    if (!inicio || !final) return 'last-30-days';
    const diff = moment(final).diff(moment(inicio), 'days');
    if (diff <= 7) return 'last-7-days';
    if (diff <= 14) return 'last-14-days';
    if (diff <= 30) return 'last-30-days';
    if (diff <= 90) return 'last-90-days';
    return 'custom';
}

const FILTROS_PLATAFORMA = ['all', 'instagram', 'facebook', 'meta'];

export default function MktCtwaPage() {
    const { t } = useTranslation('marketing');
    const { user, cliente } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();

    const inicioUrl = searchParams.get('inicio');
    const finalUrl = searchParams.get('final');

    const [period, setPeriod] = useState(() => inferirPeriodo(inicioUrl, finalUrl));
    const [customStart, setCustomStart] = useState(inicioUrl || periodoParaDatas('last-30-days').inicio);
    const [customEnd, setCustomEnd] = useState(finalUrl || periodoParaDatas('last-30-days').final);
    const [clienteId, setClienteId] = useState(searchParams.get('cliente_id') || user.clienteId);
    const [listaClientesCanais, setListaClientesCanais] = useState([]);
    const [canaisProntos, setCanaisProntos] = useState(false);
    const [temCanalWaba, setTemCanalWaba] = useState(true);
    const [loading, setLoading] = useState(true);
    const [resumo, setResumo] = useState(null);
    const [detalhes, setDetalhes] = useState(null);
    const [leadsCriados, setLeadsCriados] = useState(0);
    const [filtroPlataforma, setFiltroPlataforma] = useState('all');
    const [modoVisual, setModoVisual] = useState('cards');
    const [anuncioSelecionado, setAnuncioSelecionado] = useState(null);
    const [drawerChatsAberto, setDrawerChatsAberto] = useState(false);
    const [anuncios, setAnuncios] = useState([]);
    const [anunciosTotal, setAnunciosTotal] = useState(0);
    const [anunciosHasMore, setAnunciosHasMore] = useState(false);
    const [loadingAnuncios, setLoadingAnuncios] = useState(false);
    const [loadingMoreAnuncios, setLoadingMoreAnuncios] = useState(false);
    const [anunciosPage, setAnunciosPage] = useState(1);

    const isMultisession = cliente?.multiSession === 1;

    const dateRange = useMemo(() => {
        if (period === 'custom') {
            return { inicio: customStart, final: customEnd };
        }
        return periodoParaDatas(period);
    }, [period, customStart, customEnd]);

    const querys = useMemo(
        () => montarQuerysCtwa({ userId: user.id, clienteId, ...dateRange }),
        [user.id, clienteId, dateRange]
    );

    const periodoLabel = `${dateRange.inicio.split('-').reverse().join('/')} a ${dateRange.final.split('-').reverse().join('/')}`;

    useEffect(() => {
        setSearchParams(
            { inicio: dateRange.inicio, final: dateRange.final, cliente_id: clienteId },
            { replace: true }
        );
    }, [dateRange.inicio, dateRange.final, clienteId, setSearchParams]);

    useEffect(() => {
        let ativo = true;

        (async () => {
            setCanaisProntos(false);
            try {
                const orgId = cliente?._id || user.clienteId;

                if (isMultisession) {
                    const res = await actionListaClientesCanais(orgId);
                    if (!ativo) return;

                    const canaisWaba = filtrarCanaisWhatsappBusiness(res.data.data || []);
                    setListaClientesCanais(canaisWaba);
                    setTemCanalWaba(canaisWaba.length > 0);

                    if (canaisWaba.length) {
                        const ids = canaisWaba.map((item) => String(item.cliente_id));
                        if (!ids.includes(String(clienteId))) {
                            setClienteId(canaisWaba[0].cliente_id);
                        }
                    }
                } else {
                    const res = await actionListaCanaisPorCliente(orgId);
                    if (!ativo) return;

                    const canaisWaba = filtrarCanaisWhatsappBusiness(res.data?.data || []);
                    setTemCanalWaba(canaisWaba.length > 0);
                    if (canaisWaba.length) {
                        const ids = canaisWaba.map((item) => String(item.cliente_id));
                        if (!ids.includes(String(clienteId))) {
                            setClienteId(canaisWaba[0].cliente_id);
                        }
                    }
                }
            } catch (err) {
                console.log('MktCtwaPage canais', err);
                if (ativo) {
                    setListaClientesCanais([]);
                    setTemCanalWaba(false);
                }
            } finally {
                if (ativo) setCanaisProntos(true);
            }
        })();

        return () => {
            ativo = false;
        };
    }, [isMultisession, cliente?._id, user.clienteId]);

    useEffect(() => {
        if (!canaisProntos) return undefined;
        if (!temCanalWaba) {
            setLoading(false);
            setResumo(null);
            setDetalhes(null);
            setLeadsCriados(0);
            return undefined;
        }

        let ativo = true;
        (async () => {
            setLoading(true);
            try {
                const [resTotal, resDetalhes, resTickets] = await Promise.allSettled([
                    v2_actionTotalCtwa(querys),
                    v2_actionCtwaDetalhes(querys),
                    v2_actionTotalTickets(querys),
                ]);
                if (!ativo) return;
                if (resTotal.status === 'fulfilled') setResumo(resTotal.value.data.data);
                if (resDetalhes.status === 'fulfilled') setDetalhes(resDetalhes.value.data.data);
                if (resTickets.status === 'fulfilled') setLeadsCriados(resTickets.value.data.data?.criados ?? 0);
            } catch (err) {
                console.log('MktCtwaPage', err);
            } finally {
                if (ativo) setLoading(false);
            }
        })();
        return () => {
            ativo = false;
        };
    }, [querys, canaisProntos, temCanalWaba]);

    const carregarAnuncios = useCallback(async (pageToLoad, append = false) => {
        if (!temCanalWaba) return;
        if (append) {
            setLoadingMoreAnuncios(true);
        } else {
            setLoadingAnuncios(true);
        }

        try {
            const querysAnuncios = montarQuerysCtwaAnuncios({
                querysBase: querys,
                page: pageToLoad,
                limit: ANUNCIOS_PAGE_SIZE,
                plataforma: filtroPlataforma,
            });
            const res = await v2_actionCtwaAnuncios(querysAnuncios);
            const payload = res.data.data || {};
            const novos = payload.anuncios || [];

            setAnunciosTotal(payload.total ?? 0);
            setAnunciosHasMore(!!payload.hasMore);
            setAnunciosPage(payload.page ?? pageToLoad);
            setAnuncios((prev) => (append ? [...prev, ...novos] : novos));
        } catch (err) {
            console.log('MktCtwaPage anuncios', err);
            if (!append) {
                setAnuncios([]);
                setAnunciosTotal(0);
                setAnunciosHasMore(false);
            }
        } finally {
            setLoadingAnuncios(false);
            setLoadingMoreAnuncios(false);
        }
    }, [querys, filtroPlataforma, temCanalWaba]);

    useEffect(() => {
        if (loading || !canaisProntos || !temCanalWaba) return undefined;

        setAnunciosPage(1);
        setAnuncios([]);
        carregarAnuncios(1, false);

        return undefined;
    }, [loading, carregarAnuncios]);

    const porTipo = detalhes?.porTipo || [];
    const porDia = detalhes?.porDia || [];
    const resumoDetalhado = detalhes?.resumo;
    const contagemPlataforma = detalhes?.contagemPlataforma || { all: 0, instagram: 0, facebook: 0, meta: 0 };

    const maiorCapturaFiltrado = useMemo(
        () => Math.max(1, ...anuncios.map((item) => item.total || 0)),
        [anuncios]
    );

    const total = resumo?.total ?? resumoDetalhado?.total ?? 0;
    const contatosUnicos = resumo?.contatosUnicos ?? resumoDetalhado?.contatosUnicos ?? 0;
    const anunciosDistintos = resumoDetalhado?.anunciosDistintos ?? 0;
    const mediaPorDia = resumoDetalhado?.mediaPorDia ?? 0;

    const entradasPorContato =
        total > 0 && contatosUnicos > 0
            ? (total / contatosUnicos).toLocaleString('pt-BR', { maximumFractionDigits: 1 })
            : null;

    const pctLeads = ratio(total, leadsCriados);
    const maxTipo = Math.max(1, ...porTipo.map((tItem) => tItem.total || 0));

    const evolucaoDiaria = useMemo(() => {
        const compacto = porDia.length > 14;
        return porDia.map((item) => ({
            label: rotuloDia(item.dia, { compact: compacto }),
            fullLabel: rotuloDiaCompleto(item.dia),
            entradas: item.total || 0,
        }));
    }, [porDia]);

    const labelStepDiario = useMemo(() => {
        const n = evolucaoDiaria.length;
        if (n <= 7) return 1;
        if (n <= 14) return 2;
        if (n <= 31) return 5;
        return Math.ceil(n / 8);
    }, [evolucaoDiaria.length]);

    const rotuloFiltro = (id) => {
        const base = t(`ctwaPage.filters.${id}`);
        const n = contagemPlataforma[id];
        return n ? `${base} (${n})` : base;
    };

    const abrirChatsAnuncio = (anuncio) => {
        setAnuncioSelecionado(anuncio);
        setDrawerChatsAberto(true);
    };

    const semCanalWaba = canaisProntos && !temCanalWaba;

    return (
        <div className={d.page.root} style={{ fontFamily: d.page.fontStack }}>
            <div className={d.page.stickyHeader}>
                <div className={d.page.container}>
                    <div className={d.page.topbar}>
                        <div className={d.page.topbarIdentity}>
                            <div className={d.page.iconTileWhatsapp}>
                                <Iconify icon="ic:round-whatsapp" width={22} />
                            </div>
                            <div className="min-w-0">
                                <h1 className={d.page.headerTitle}>{t('ctwaPage.title')}</h1>
                                <p className={d.page.headerSubtitle}>{t('ctwaPage.subtitle')}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className={`${d.page.container} pt-4 ${d.page.pageBody}`}>
                <div className={d.page.filterBar}>
                    <PeriodFilter
                        period={period}
                        onPeriodChange={setPeriod}
                        startDate={customStart}
                        endDate={customEnd}
                        onStartDateChange={setCustomStart}
                        onEndDateChange={setCustomEnd}
                        labelClassName={d.page.filterLabel}
                        controlClassName={d.page.filterSelect}
                    />
                    {isMultisession && listaClientesCanais.length > 0 && (
                        <div className={d.page.filterItem}>
                            <span className={d.page.filterLabel}>{t('ctwaPage.channel')}</span>
                            <Select value={clienteId} onValueChange={setClienteId}>
                                <SelectTrigger className={d.page.filterSelect}>
                                    <SelectValue placeholder={t('ctwaPage.allChannels')} />
                                </SelectTrigger>
                                <SelectContent className="max-h-[300px] z-[99999]" position="popper" sideOffset={4}>
                                    {listaClientesCanais.map((item) => (
                                        <SelectItem key={item.cliente_id} value={item.cliente_id}>
                                            {item.nome}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                </div>

                {!semCanalWaba && !loading && canaisProntos && total > 0 && (
                    <p className={d.page.periodHint}>{periodoLabel}</p>
                )}

                {loading || !canaisProntos ? (
                    <MktLoading />
                ) : semCanalWaba ? (
                    <CtwaWabaHero />
                ) : total === 0 ? (
                    <MktEmptyState
                        icon="ic:round-whatsapp"
                        title={t('ctwaPage.emptyTitle')}
                        subtitle={t('ctwaPage.emptyDescription')}
                    />
                ) : (
                    <div className={d.page.stack}>
                        <div className={d.statsGrid}>
                            {[
                                { val: fmtInt(total), lbl: t('ctwaPage.stats.total') },
                                { val: fmtInt(contatosUnicos), lbl: t('ctwaPage.stats.contacts') },
                                { val: fmtInt(anunciosDistintos), lbl: t('ctwaPage.stats.ads') },
                                { val: mediaPorDia?.toLocaleString('pt-BR', { maximumFractionDigits: 1 }) ?? '—', lbl: t('ctwaPage.stats.dailyAvg') },
                            ].map((s) => (
                                <div key={s.lbl} className={d.statCell}>
                                    <div className={d.statVal}>{s.val}</div>
                                    <div className={d.statLbl}>{s.lbl}</div>
                                </div>
                            ))}
                        </div>

                        {entradasPorContato && (
                            <p className={d.insight}>
                                {t('ctwaPage.insightPerContact', { value: entradasPorContato })}
                                {pctLeads !== null && (
                                    <>
                                        {' '}
                                        {t('ctwaPage.insightLeads', { leads: fmtInt(leadsCriados), pct: fmtPct(pctLeads) })}
                                    </>
                                )}
                            </p>
                        )}

                        <div className={d.chartRow}>
                            <div className={d.section.wrapper}>
                                <h3 className={d.section.title}>{t('ctwaPage.dailyChart')}</h3>
                                {evolucaoDiaria.length ? (
                                    <div className="mt-4">
                                        <ColumnChart
                                            data={evolucaoDiaria}
                                            series={[{ key: 'entradas', label: t('ctwaPage.stats.total'), color: T.brand }]}
                                            height={200}
                                            labelStep={labelStepDiario}
                                            labelTruncate={false}
                                            showAxis
                                            barWidth={18}
                                        />
                                        <p className="text-[11px] text-[#a0a0af] mt-2">
                                            {t('ctwaPage.dailyChartHint')}
                                        </p>
                                    </div>
                                ) : (
                                    <p className="text-[13px] text-[#8b8b9a] mt-3">{t('ctwaPage.noDailyData')}</p>
                                )}
                            </div>

                            <div className={d.section.wrapper}>
                                <h3 className={d.section.title}>{t('ctwaPage.bySource')}</h3>
                                <p className={d.section.subtitle}>{t('ctwaPage.bySourceSubtitle')}</p>
                                <div className="flex flex-col gap-3 mt-4">
                                    {porTipo.map((item) => (
                                        <BarRow
                                            key={item.tipo}
                                            label={rotuloTipo(item.tipo, t)}
                                            value={fmtInt(item.total)}
                                            pct={(item.total / maxTipo) * 100}
                                            color={T.brand3}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className={d.section.wrapper}>
                            <div className={d.section.headerRow}>
                                <div>
                                    <h3 className={d.section.title}>{t('ctwaPage.byAd')}</h3>
                                    <p className={d.section.subtitle}>{t('ctwaPage.byAdCardsSubtitle')}</p>
                                </div>
                                <div className={d.viewToggle}>
                                    <button
                                        type="button"
                                        className={`${d.viewBtn} ${modoVisual === 'cards' ? d.viewBtnActive : ''}`}
                                        onClick={() => setModoVisual('cards')}
                                    >
                                        {t('ctwaPage.view.cards')}
                                    </button>
                                    <button
                                        type="button"
                                        className={`${d.viewBtn} ${modoVisual === 'lista' ? d.viewBtnActive : ''}`}
                                        onClick={() => setModoVisual('lista')}
                                    >
                                        {t('ctwaPage.view.list')}
                                    </button>
                                </div>
                            </div>

                            <div className={`${d.filterBar} mb-4`}>
                                {FILTROS_PLATAFORMA.map((id) => (
                                    <button
                                        key={id}
                                        type="button"
                                        className={`${d.filterTab} ${filtroPlataforma === id ? d.filterTabActive : ''}`}
                                        onClick={() => setFiltroPlataforma(id)}
                                    >
                                        {rotuloFiltro(id)}
                                    </button>
                                ))}
                            </div>

                            {!loadingAnuncios && !anuncios.length ? (
                                <p className="text-[13px] text-[#8b8b9a]">{t('ctwaPage.noAdsFilter')}</p>
                            ) : loadingAnuncios ? (
                                <p className="text-[13px] text-[#8b8b9a] py-6 text-center">{t('common.loading')}</p>
                            ) : modoVisual === 'lista' ? (
                                <CtwaAnuncioList anuncios={anuncios} maxTotal={maiorCapturaFiltrado} onItemClick={abrirChatsAnuncio} />
                            ) : (
                                <div className={d.adGrid}>
                                    {anuncios.map((item, index) => (
                                        <CtwaAnuncioCard
                                            key={item.source_id}
                                            anuncio={item}
                                            index={index}
                                            maxTotal={maiorCapturaFiltrado}
                                            onClick={() => abrirChatsAnuncio(item)}
                                        />
                                    ))}
                                </div>
                            )}

                            {!loadingAnuncios && anuncios.length > 0 && (
                                <div className="mt-5 pt-4 border-t border-[#eeeef2]">
                                    <p className="text-[11px] text-[#8b8b9a] text-center mb-2">
                                        {t('ctwaPage.adsPagination.showing', { count: anuncios.length, total: fmtInt(anunciosTotal) })}
                                    </p>
                                    {anunciosHasMore && (
                                        <button
                                            type="button"
                                            disabled={loadingMoreAnuncios}
                                            onClick={() => carregarAnuncios(anunciosPage + 1, true)}
                                            className="w-full py-2.5 text-[13px] font-semibold text-[#7C3AED] hover:bg-violet-50 rounded-xl transition-colors disabled:opacity-50"
                                        >
                                            {loadingMoreAnuncios ? t('ctwaPage.adsPagination.loadingMore') : t('ctwaPage.adsPagination.loadMore')}
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>

                        <CtwaAnuncioChatsDrawer
                            open={drawerChatsAberto}
                            onOpenChange={setDrawerChatsAberto}
                            anuncio={anuncioSelecionado}
                            querysBase={querys}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
